/* Extraído de index.jsx (refatoração 4.5) — widgets visuais básicos + estilos.
   Código movido VERBATIM, sem mudança de lógica. */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { SERVICES, STRATEGIES, sum, fmt1, serviceById, uniqueServices, strategyById, MEAL_ORDER, MEAL_ICONS, mealIcon, nomeRefeicao, empty } from './helpers';
import { Ico, emo } from '../ui/Ico';
/* ════════════════════════════════════════════════════════
   BARRA DE TOTAIS
════════════════════════════════════════════════════════ */
export function TotalsBar({ totals, water, aiModel }) {
  const calProt  = totals.prot * 4;
  const calCarb  = totals.carb * 4;
  const calFat   = totals.fat  * 9;
  const total    = calProt + calCarb + calFat || 1;
  const pProt    = Math.round(calProt / total * 100);
  const pCarb    = Math.round(calCarb / total * 100);
  const pFat     = 100 - pProt - pCarb;

  return (
    <div style={S.totalsBar} className="totals-bar">
      <div style={{ display:'flex', alignItems:'baseline', gap:'0.5rem', marginBottom:'0.75rem' }}>
        <span style={S.totalCal}>{Math.round(totals.cal)}</span>
        <span style={S.totalLbl}>kcal / dia</span>
      </div>
      <div style={S.macroRow}>
        <MacroBlock icon="💪" label="Proteína"  grams={fmt1(totals.prot)} pct={pProt} color="#7189a6" />
        <MacroBlock icon="🌾" label="Carboidr." grams={fmt1(totals.carb)} pct={pCarb} color="#b9d2bd" />
        <MacroBlock icon="🥑" label="Gordura"   grams={fmt1(totals.fat)}  pct={pFat}  color="#e8c9a8" />
        <MacroBlock icon="💧" label="Água rec."  grams={`${water}L`}      pct={null}  color="#b8c7d8" />
      </div>
      <div style={S.macroBar} className="no-print">
        <div style={{ width:`${pProt}%`, background:'#7189a6', height:'100%' }} />
        <div style={{ width:`${pCarb}%`, background:'#b9d2bd', height:'100%' }} />
        <div style={{ width:`${pFat}%`,  background:'#e8c9a8', height:'100%' }} />
      </div>
      {aiModel && <div style={S.aiModel} className="no-print"><Ico e="✨" /> {aiModel}</div>}
    </div>
  );
}

export function MacroBlock({ icon, label, grams, pct, color }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
      <span>{emo(icon)}</span>
      <div>
        <div style={{ fontWeight:600, fontSize:'0.95rem', color:'#2f2a26' }}>
          {grams}{pct !== null ? ` (${pct}%)` : ''}
        </div>
        <div style={{ fontSize:'0.73rem', color:'#736b64' }}>{emo(label)}</div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   MEAL CARD
════════════════════════════════════════════════════════ */
/**
 * Célula de porção inteligente (4.5): quando a porção é "número + unidade"
 * ("150g", "200 ml", "2 fatias"), edita a QUANTIDADE numericamente e reescala
 * kcal/prot/carb/gord proporcionalmente (linear em qualquer unidade). O valor
 * SALVO continua sendo a mesma string de sempre — impressão, PDF e planos
 * antigos não mudam. Porção não-parseável ("a gosto") permanece texto livre.
 */
function PorcaoCell({ food, onChange }) {
  const m = String(food.porcao ?? '').match(/^\s*(\d+(?:[.,]\d+)?)(\s*)(.*)$/);

  if (!m) {
    return (
      <input style={S.cellIn} value={food.porcao}
        onChange={e => onChange('porcao', e.target.value)} />
    );
  }

  const qty  = parseFloat(m[1].replace(',', '.'));
  const sep  = m[2];
  const unit = m[3];

  const onQty = (e) => {
    const raw = e.target.value;
    if (raw === '') { onChange('porcao', ''); return; } // vira texto livre
    const nq = parseFloat(raw);
    if (!isFinite(nq) || nq < 0) return;
    onChange('porcao', `${nq}${unit ? sep + unit : ''}`.trim());
    if (qty > 0 && nq !== qty) {
      const f = nq / qty;
      onChange('calorias',     Math.round((parseFloat(food.calorias)     || 0) * f));
      onChange('proteina',     fmt1((parseFloat(food.proteina)     || 0) * f));
      onChange('carboidratos', fmt1((parseFloat(food.carboidratos) || 0) * f));
      onChange('gordura',      fmt1((parseFloat(food.gordura)      || 0) * f));
    }
  };

  const onUnit = (e) => {
    onChange('porcao', `${qty}${e.target.value ? sep + e.target.value : ''}`.trim());
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}
      title="Alterar a quantidade recalcula kcal e macros proporcionalmente">
      <input style={{ ...S.cellIn, ...S.numIn, width: '54px' }} type="number" min="0" step="any"
        value={qty} onChange={onQty} />
      <input style={{ ...S.cellIn, width: '58px' }} value={unit} placeholder="g"
        onChange={onUnit} />
    </div>
  );
}

export function MealCard({ mealKey, meal, onUpdateFood, onAddFood, onRemoveFood, onSubstitute, onBrowseBank }) {
  const [collapsed, setCollapsed] = useState(false);
  const foods = meal.alimentos || [];
  const mCal  = sum(foods, 'calorias');

  return (
    <div style={S.mealCard} className="meal-card">
      <div style={S.mealHead} onClick={() => setCollapsed(c => !c)}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
          <span style={{ fontSize:'1.5rem' }}>{emo(mealIcon(mealKey))}</span>
          <div>
            <div style={{ fontWeight:600, fontSize:'0.95rem', color:'#2f2a26' }}>{nomeRefeicao(mealKey, meal)}</div>
            <div style={{ fontSize:'0.78rem', color:'#736b64' }}>{meal.horario_sugerido}</div>
            {meal.meta && (() => {
              // Meta da refeição (definida no passo "Refeições do plano") × realizado
              const dif = meal.meta.kcal ? (mCal - meal.meta.kcal) / meal.meta.kcal * 100 : 0;
              const fora = Math.abs(dif) > 10;
              return (
                <div className="no-print" style={{ fontSize:'0.74rem', marginTop:2, color: fora ? '#8a5f1a' : '#2f5236' }}
                     title="Meta calculada pelo sistema para esta refeição">
                  meta {meal.meta.kcal} kcal · P {meal.meta.proteina} g · C {meal.meta.carboidratos} g · G {meal.meta.gordura} g
                  {' '}({dif >= 0 ? '+' : ''}{Math.round(dif)}%{fora ? ' — revisar' : ''})
                </div>
              );
            })()}
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
          <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap' }} className="no-print">
            <MacroPill v={`${mCal} kcal`}              c="#D4A574" />
            <MacroPill v={`${fmt1(sum(foods,'proteina'))}g P`} c="#7189a6" />
            <MacroPill v={`${fmt1(sum(foods,'carboidratos'))}g C`} c="#b9d2bd" />
            <MacroPill v={`${fmt1(sum(foods,'gordura'))}g G`} c="#e8c9a8" />
          </div>
          <span style={{ color:'#d9d0c6', fontSize:'0.85rem' }} className="no-print">
            {collapsed ? '▶' : '▼'}
          </span>
        </div>
      </div>

      {!collapsed && (
        <div style={{ padding:'0 1.5rem 1.25rem' }}>
          <div style={{ overflowX:'auto' }}>
            <table style={S.fTable}>
              <thead>
                <tr>
                  {['Alimento','Porção','Kcal','Prot.(g)','Carb.(g)','Gord.(g)',''].map(h => (
                    <th key={h} style={{ ...S.fth, ...(h === '' ? { width: '60px' } : {}) }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {foods.map((food, idx) => (
                  <tr key={idx}>
                    <td style={S.ftd}>
                      <input style={S.cellIn} value={food.alimento}
                        onChange={e => onUpdateFood(mealKey, idx, 'alimento', e.target.value)} />
                    </td>
                    <td style={S.ftd}>
                      <PorcaoCell food={food} onChange={(field, value) => onUpdateFood(mealKey, idx, field, value)} />
                    </td>
                    <td style={S.ftd}>
                      <input style={{ ...S.cellIn, ...S.numIn }} type="number" min="0" value={food.calorias}
                        onChange={e => onUpdateFood(mealKey, idx, 'calorias', e.target.value)} />
                    </td>
                    <td style={S.ftd}>
                      <input style={{ ...S.cellIn, ...S.numIn }} type="number" min="0" step="0.1" value={food.proteina}
                        onChange={e => onUpdateFood(mealKey, idx, 'proteina', e.target.value)} />
                    </td>
                    <td style={S.ftd}>
                      <input style={{ ...S.cellIn, ...S.numIn }} type="number" min="0" step="0.1" value={food.carboidratos}
                        onChange={e => onUpdateFood(mealKey, idx, 'carboidratos', e.target.value)} />
                    </td>
                    <td style={S.ftd}>
                      <input style={{ ...S.cellIn, ...S.numIn }} type="number" min="0" step="0.1" value={food.gordura}
                        onChange={e => onUpdateFood(mealKey, idx, 'gordura', e.target.value)} />
                    </td>
                    <td style={S.ftd} className="no-print">
                      <div style={{ display:'flex', gap:'2px', alignItems:'center' }}>
                        {onSubstitute && (
                          <button style={S.subBtn}
                            title="Substituir alimento"
                            onClick={() => onSubstitute(mealKey, idx, food)}><Ico e="🔄" /></button>
                        )}
                        <button style={S.rmBtn} onClick={() => onRemoveFood(mealKey, idx)}><Ico e="✕" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }} className="no-print">
            {onBrowseBank && (
              <button style={{ ...S.addFoodBtn, borderStyle:'solid', background:'#fbf8f5' }}
                onClick={() => onBrowseBank(mealKey)}>
                <Ico e="🥩" sp />Buscar no banco
              </button>
            )}
            <button style={S.addFoodBtn} onClick={() => onAddFood(mealKey)}>
              + Linha em branco
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function MacroPill({ v, c }) {
  return (
    <span style={{ background:`${c}22`, color:c, fontSize:'0.75rem', fontWeight:600,
      padding:'2px 7px', borderRadius:'20px', whiteSpace:'nowrap' }}>
      {v}
    </span>
  );
}

/* ════════════════════════════════════════════════════════
   BADGES
════════════════════════════════════════════════════════ */
export function ServiceBadge({ serviceId, small }) {
  const svc = serviceById(serviceId);
  if (!svc) return null;
  return (
    <span style={{
      background: svc.bg, color: svc.color,
      border: `1px solid ${svc.color}40`,
      fontSize: small ? '0.72rem' : '0.8rem',
      fontWeight: 600,
      padding: small ? '2px 8px' : '3px 10px',
      borderRadius: '20px',
      whiteSpace: 'nowrap',
      display: 'inline-flex', alignItems: 'center', gap: '4px',
    }}>
      {emo(svc.icon)} {emo(svc.label)}
    </span>
  );
}

export function StrategyBadge({ strategyId, small }) {
  const st = strategyById(strategyId);
  if (!st) return null;
  return (
    <span style={{
      background: '#f6f0ea', color: '#c4933f',
      border: '1px solid #E67E2240',
      fontSize: small ? '0.72rem' : '0.8rem',
      fontWeight: 600,
      padding: small ? '2px 8px' : '3px 10px',
      borderRadius: '20px',
      whiteSpace: 'nowrap',
      display: 'inline-flex', alignItems: 'center', gap: '4px',
    }}>
      {emo(st.icon)} {emo(st.label)}
    </span>
  );
}

/* ════════════════════════════════════════════════════════
   UTILITÁRIOS
════════════════════════════════════════════════════════ */
export function DataChip({ label, note }) {
  return (
    <span style={{ background:'#f6f0ea', color:'#46714f', fontSize:'0.8rem', fontWeight:600,
      padding:'3px 12px', borderRadius:'20px', border:'1px solid #b9d2bd' }}>
      <Ico e="✓" /> {emo(label)}{note ? ` (${note})` : ''}
    </span>
  );
}

export function Spinner() {
  return (
    <span style={{ display:'inline-block', width:'17px', height:'17px',
      border:'2px solid rgba(255,255,255,0.4)', borderTopColor:'#ffffff',
      borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
  );
}

/* PrintCSS is now imported from ./PrintLayout */

/* ══════════════════════════════════════════════════════
   ESTILOS
══════════════════════════════════════════════════════ */
export const S = {
  page:       { padding:'2rem', maxWidth:'1100px', margin:'0 auto' },
  header:     { display:'flex', justifyContent:'space-between', alignItems:'flex-start',
                marginBottom:'1.5rem', flexWrap:'wrap', gap:'1rem' },
  title:      { fontSize:'1.75rem', fontWeight:600, color:'#2f2a26', margin:'0 0 0.25rem' },
  subtitle:   { color:'#736b64', fontSize:'0.88rem', margin:0 },
  backBtn:    { background:'none', border:'none', color:'#D4A574', cursor:'pointer',
                fontSize:'0.9rem', padding:'0 0 0.5rem', display:'block' },
  card:       { background:'#ffffff', borderRadius:'12px', padding:'1.5rem',
                marginBottom:'1.25rem', boxShadow:'0 2px 8px rgba(44,44,44,0.07)' },
  cardTitle:  { fontSize:'1rem', fontWeight:600, color:'#2f2a26', marginTop:0, marginBottom:'1rem' },
  label:      { display:'block', fontSize:'0.84rem', fontWeight:600, color:'#4a4440', marginBottom:'0.35rem' },
  select:     { width:'100%', maxWidth:'400px', padding:'0.65rem 1rem', border:'1.5px solid #d9d0c6',
                borderRadius:'8px', fontSize:'0.95rem', color:'#2f2a26', background:'#ffffff', outline:'none' },
  input:      { width:'100%', padding:'0.65rem 1rem', border:'1.5px solid #d9d0c6',
                borderRadius:'8px', fontSize:'0.95rem', color:'#2f2a26', boxSizing:'border-box', outline:'none' },
  textarea:   { width:'100%', padding:'0.65rem 1rem', border:'1.5px solid #d9d0c6',
                borderRadius:'8px', fontSize:'0.9rem', color:'#2f2a26', resize:'vertical',
                boxSizing:'border-box', outline:'none', minHeight:'80px' },
  grid2:      { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'1rem' },
  field:      { display:'flex', flexDirection:'column' },
  tabs:       { display:'flex', gap:'0.5rem', marginBottom:'1rem', flexWrap:'wrap' },
  tab:        { padding:'0.6rem 1.25rem', border:'1.5px solid #d9d0c6', borderRadius:'8px',
                background:'#ffffff', color:'#736b64', cursor:'pointer', fontSize:'0.9rem', fontWeight:500 },
  tabActive:  { background:'linear-gradient(135deg,#D4A574,#e8c9a8)', color:'#ffffff', borderColor:'transparent' },
  errorBox:   { background:'#f8ebe9', color:'#a8483f', padding:'0.75rem 1rem', borderRadius:'8px',
                marginBottom:'1rem', fontSize:'0.9rem' },
  successBox: { background:'#edf4ee', color:'#46714f', padding:'0.75rem 1rem', borderRadius:'8px',
                marginBottom:'1rem', fontSize:'0.9rem' },
  emptyMsg:   { color:'#736b64', textAlign:'center', padding:'2rem 0' },
  emptyState: { textAlign:'center', padding:'2.5rem 0', color:'#736b64' },
  primaryBtn: { background:'linear-gradient(135deg,#D4A574,#e8c9a8)', color:'#ffffff', border:'none',
                borderRadius:'8px', padding:'0.7rem 1.75rem', fontSize:'0.95rem', fontWeight:600, cursor:'pointer' },
  saveBtn:    { background:'linear-gradient(135deg,#D4A574,#e8c9a8)', color:'#ffffff', border:'none',
                borderRadius:'8px', padding:'0.65rem 1.5rem', fontSize:'0.95rem', fontWeight:600, cursor:'pointer' },
  outlineBtn: { background:'#f6f0ea', color:'#4a4440', border:'none', borderRadius:'8px',
                padding:'0.65rem 1.25rem', fontSize:'0.9rem', cursor:'pointer' },
  generateBtn:{ background:'linear-gradient(135deg,#7189a6,#b8c7d8)', color:'#ffffff', border:'none',
                borderRadius:'10px', padding:'1rem 2.5rem', fontSize:'1rem', fontWeight:600,
                cursor:'pointer', display:'block', margin:'0 auto', minWidth:'300px' },

  /* Seletor de serviço */
  serviceGrid:  { display:'flex', flexDirection:'column', gap:'0.6rem' },
  serviceCard:  { display:'flex', alignItems:'flex-start', gap:'0.75rem', padding:'0.85rem 1rem',
                  borderRadius:'10px', cursor:'pointer', textAlign:'left', width:'100%',
                  transition:'all 0.15s' },
  strategyGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:'0.6rem' },
  strategyCard: { display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.65rem 1rem',
                  borderRadius:'8px', cursor:'pointer', fontSize:'0.9rem', fontWeight:500,
                  transition:'all 0.15s', width:'100%' },

  /* Totals bar */
  totalsBar: { background:'#ffffff', borderRadius:'12px', padding:'1.25rem 1.5rem',
               marginBottom:'1.25rem', boxShadow:'0 2px 8px rgba(44,44,44,0.07)' },
  totalCal:  { fontSize:'2.5rem', fontWeight:700, color:'#D4A574' },
  totalLbl:  { fontSize:'1rem', color:'#736b64', fontWeight:500 },
  macroRow:  { display:'flex', gap:'1.5rem', flexWrap:'wrap', marginBottom:'0.75rem' },
  macroBar:  { height:'8px', borderRadius:'4px', overflow:'hidden',
               background:'#ebe3da', display:'flex' },
  aiModel:   { fontSize:'0.73rem', color:'#b8c7d8', marginTop:'0.4rem', fontWeight:600 },

  /* Meal card */
  mealCard:  { background:'#ffffff', borderRadius:'12px', marginBottom:'1rem',
               boxShadow:'0 2px 8px rgba(44,44,44,0.07)', overflow:'hidden' },
  mealHead:  { display:'flex', alignItems:'center', justifyContent:'space-between',
               padding:'1rem 1.5rem', cursor:'pointer', userSelect:'none' },

  /* Food table */
  fTable:  { width:'100%', borderCollapse:'collapse', fontSize:'0.87rem', marginBottom:'0.75rem' },
  fth:     { padding:'0.5rem', textAlign:'left', background:'#fbf8f5',
             borderBottom:'2px solid #ebe3da', fontWeight:600, color:'#736b64', whiteSpace:'nowrap' },
  ftd:     { padding:'0.3rem 0.25rem', borderBottom:'1px solid #f6f0ea' },
  cellIn:  { border:'1px solid #ebe3da', borderRadius:'6px', padding:'0.3rem 0.5rem',
             fontSize:'0.87rem', width:'100%', background:'#fbf8f5', color:'#2f2a26',
             outline:'none', minWidth:'70px' },
  numIn:   { width:'65px', textAlign:'right', minWidth:'55px' },
  rmBtn:   { background:'none', border:'none', color:'#e8c9a8', cursor:'pointer',
             fontSize:'0.9rem', padding:'2px 4px' },
  addFoodBtn: { background:'none', border:'1.5px dashed #D4A574', color:'#D4A574',
                borderRadius:'8px', padding:'0.45rem 1rem', fontSize:'0.87rem',
                cursor:'pointer', fontWeight:600 },

  /* Supplements */
  supGrid:     { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'0.85rem' },
  supCard:     { background:'#fbf8f5', border:'1.5px solid #ebe3da', borderRadius:'10px',
                 padding:'0.9rem', display:'flex', flexDirection:'column', gap:'0.5rem' },
  supCardTop:  { display:'flex', alignItems:'flex-start', gap:'0.5rem' },
  supNameIn:   { border:'none', background:'transparent', fontSize:'0.95rem', fontWeight:600,
                 color:'#2f2a26', outline:'none', width:'100%', padding:'2px 0' },
  supRemoveBtn:{ background:'none', border:'none', color:'#e8c9a8', cursor:'pointer',
                 fontSize:'0.85rem', padding:'2px 4px', flexShrink:0, lineHeight:1 },
  supRow:      { display:'flex', gap:'0.6rem' },
  supField:    { display:'flex', flexDirection:'column', gap:'2px', flex:1 },
  supFieldLbl: { fontSize:'0.68rem', fontWeight:600, textTransform:'uppercase',
                 letterSpacing:'.05em', color:'#a39b93' },
  supSmallIn:  { border:'1px solid #ebe3da', borderRadius:'6px', padding:'0.3rem 0.5rem',
                 fontSize:'0.83rem', color:'#4a4440', outline:'none', background:'#ffffff',
                 width:'100%', boxSizing:'border-box' },
  addSupBtn:   { background:'none', border:'1.5px dashed #D4A574', color:'#D4A574',
                 borderRadius:'8px', padding:'0.35rem 0.85rem', fontSize:'0.83rem',
                 cursor:'pointer', fontWeight:600 },

  /* Substitute button in food row */
  subBtn: { background:'none', border:'none', color:'#b8c7d8', cursor:'pointer',
            fontSize:'0.85rem', padding:'2px 3px', title:'Substituir' },

  /* Food database panel */
  foodPanel: { width:'320px', flexShrink:0, background:'#ffffff', borderRadius:'12px',
               padding:'1rem', boxShadow:'0 2px 8px rgba(44,44,44,0.07)',
               display:'flex', flexDirection:'column', maxHeight:'85vh',
               position:'sticky', top:'1rem', gap:'0' },
  fpItem:    { display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.5rem 0.6rem',
               background:'#fbf8f5', borderRadius:'8px', border:'1px solid #ebe3da' },
  fpAddBtn:  { background:'#D4A574', color:'#ffffff', border:'none', borderRadius:'8px',
               width:'40px', height:'40px', fontSize:'1.3rem', cursor:'pointer',
               display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
               touchAction:'manipulation', position:'relative', zIndex:1 },

  /* Modals */
  modalOverlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1300,
                  display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' },
  modalBox:     { background:'#ffffff', borderRadius:'16px', padding:'1.5rem',
                  width:'100%', maxWidth:'420px', boxShadow:'0 8px 32px rgba(0,0,0,0.18)' },
  modalClose:   { background:'none', border:'none', color:'#d9d0c6', cursor:'pointer',
                  fontSize:'1rem', padding:'2px 6px', lineHeight:1 },

  /* Substitute items */
  subItem:   { display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.6rem 0.75rem',
               background:'#fbf8f5', borderRadius:'8px', border:'1px solid #ebe3da' },
  subUseBtn: { background:'linear-gradient(135deg,#D4A574,#e8c9a8)', color:'#ffffff', border:'none',
               borderRadius:'7px', padding:'0.35rem 0.75rem', fontSize:'0.82rem',
               cursor:'pointer', fontWeight:600, flexShrink:0, whiteSpace:'nowrap' },

  /* History */
  historyItem: { display:'flex', alignItems:'center', gap:'1rem', padding:'1rem',
                 background:'#fbf8f5', borderRadius:'10px', border:'1px solid #ebe3da',
                 flexWrap:'wrap' },
  historyName: { fontWeight:600, color:'#2f2a26', fontSize:'0.95rem' },
  historyMeta: { display:'flex', gap:'1rem', fontSize:'0.8rem', color:'#736b64',
                 flexWrap:'wrap', marginTop:'0.25rem' },
  aiTag:       { background:'#edf1f6', color:'#7189a6', fontSize:'0.72rem',
                 fontWeight:600, padding:'2px 8px', borderRadius:'20px' },
  statusTag:   { fontSize:'0.72rem', fontWeight:600, padding:'2px 8px', borderRadius:'20px' },
  openBtn:     { background:'linear-gradient(135deg,#D4A574,#e8c9a8)', color:'#ffffff',
                 border:'none', borderRadius:'8px', padding:'0.45rem 1rem',
                 fontSize:'0.85rem', cursor:'pointer', whiteSpace:'nowrap' },
  delBtn:      { background:'#f8ebe9', color:'#c46a60', border:'none',
                 borderRadius:'8px', padding:'0.45rem 0.65rem', cursor:'pointer' },
};
